/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    canPlaceOrder,
    canSubmitForApprovalOrder,
    getCartState,
    getCurrentCartState,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import { CheckoutReviewAndSubmitPageContext } from "@insite/content-library/Pages/CheckoutReviewAndSubmitPage";
import { CheckoutShippingPageContext } from "@insite/content-library/Pages/CheckoutShippingPage";
import PlaceOrderButton from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/CheckoutReviewAndSubmitPlaceOrderButton";
import SubmitForApprovalButton from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/CheckoutReviewAndSubmitSubmitForApprovalButton";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { StepperContext } from "@monsma/App/Widgets/CheckoutStepper/StepperContext";
import { screen } from "@monsma/XDL/Helpers";
import { doScroll } from "@monsma/XDL/Utilities";
import React, { useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const { cartId } = state.pages.checkoutReviewAndSubmit;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    return {
        checkoutShippingPage: getPageLinkByPageType(state, "CheckoutShippingPage"),
        cartPage: getPageLinkByPageType(state, "CartPage"),
        isPickUp: state.context.session.fulfillmentMethod === FulfillmentMethod.PickUp,
        isLoading: cartState.isLoading,
        cart: cartState.value,
        showPlaceOrderButton: canPlaceOrder(cartState.value),
        showSubmitForApprovalOrder: canSubmitForApprovalOrder(getCurrentCartState(state).value),
    };
};

const mapDispatchToProps = {};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface CheckoutStepperStyles {
    container?: InjectableCss;
    prev?: ButtonPresentationProps;
    next?: ButtonPresentationProps;
}

export const styles: CheckoutStepperStyles = {
    container: {
        css: css`
            display: flex;
            justify-content: space-between;
            //margin-top: 30px;
            padding-top: 30px;
            border-top: 1px solid ${getColor("common.ui12")};
            ${screen("lt", "sm")} {
                flex-direction: column;
            }
        `,
    },
    prev: {
        variant: "secondary",
        css: css`
            ${screen("lt", "sm")} {
                margin-bottom: 15px;
            }
        `,
    },
    next: {
        variant: "primary",
        css: css`
            ${screen("lt", "sm")} {
                margin-bottom: 15px;
            }
        `,
    },
};

const CheckoutStepper: React.FC<Props> = ({
    history,
    isPickUp,
    cartPage,
    checkoutShippingPage,
    isLoading,
    cart,
    showPlaceOrderButton,
    showSubmitForApprovalOrder,
}) => {
    const { currentStep, maxStep, setCurrentStep } = useContext(StepperContext);

    const useCartIcon = true;

    // TODO: make steps in the context to be shared between stepper and actions
    // if pickup empty hide and move to the next step
    const steps: any = ["Cart", "Addresses", isPickUp ? "" : "Shipping", "Payment", "Review"];

    const goNext = () => {
        if (currentStep !== 1) {
            setCurrentStep(currentStep + 1);
        }
        // if (window.innerWidth <= 768) {
        doScroll(true);
        // } else {
        //     doScroll();
        // }
    };

    const goBack = () => {
        if (currentStep === 1) {
            history.push(cartPage?.url as string);
        } else if (currentStep === 2 || (currentStep === 3 && isPickUp)) {
            history.push(checkoutShippingPage?.url as string);
        } else {
            setCurrentStep(currentStep - 1);
        }
        doScroll(true);
        // if (window.innerWidth <= 768) {
        //     window.scrollTo(0, 0);
        // }
    };

    return (
        <StyledWrapper {...styles.container}>
            <Button {...styles.prev} onClick={goBack} disabled={currentStep < 0 || isLoading} type="button">
                {translate("Back to ") + (steps[currentStep - 1] || steps[currentStep - 2])}
            </Button>
            {currentStep !== 4 && (
                <Button
                    {...styles.next}
                    onClick={goNext}
                    disabled={currentStep < 0 || isLoading}
                    type={currentStep === 1 ? "submit" : "button"}
                >
                    {translate("Continue to ") + (steps[currentStep + 1] || steps[currentStep + 2])}
                </Button>
            )}
            {currentStep === 4 && cart && showPlaceOrderButton && <PlaceOrderButton styles={styles.next} />}
            {currentStep === 4 && cart && showSubmitForApprovalOrder && (
                <SubmitForApprovalButton extendedStyles={styles.next} />
            )}
        </StyledWrapper>
    );
};
export const definition: WidgetDefinition = {
    group: "Checkout - Shipping",
    allowedContexts: [CheckoutShippingPageContext, CheckoutReviewAndSubmitPageContext],
    displayName: "Checkout Stepper Actions",
    fieldDefinitions: [],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(CheckoutStepper)),
    definition,
};

export default widgetModule;
